import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Button } from "./ui/button";
import { Save, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { stageLabel } from "../lib/crmStages";

const STAGES = ["new", "contacted", "interested", "ordering", "inactive", "lost"];
const TYPES = ["doctor", "distributor", "retailer", "hospital", "patient", "other"];
const SOURCES = ["field-visit", "referral", "doctor-conference", "phone-inbound", "whatsapp", "website", "other"];

const blank = {
    name: "", contact_type: "doctor", channel: "b2b", phone: "", whatsapp: "", email: "", company: "",
    city: "", state: "", address: "", stage: "new", tags: [], assigned_mr_id: "",
    customer_master_id: "", source: "", notes: "", next_follow_up_at: "", estimated_value: 0,
};

export default function CRMContactDialog({ open, onOpenChange, contact, defaultChannel = "b2b", onSaved }) {
    const { user } = useAuth();
    const canReassign = user?.role === "admin" || user?.role === "editor";
    const [form, setForm] = useState(blank);
    const [mrs, setMrs] = useState([]);
    const [tagInput, setTagInput] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!open) return;
        api.get("/crm/mrs").then((r) => setMrs(r.data)).catch(() => setMrs([]));
        setForm(contact
            ? { ...blank, ...contact, tags: contact.tags || [], assigned_mr_id: contact.assigned_mr_id || "" }
            : { ...blank, channel: defaultChannel, contact_type: defaultChannel === "ecommerce" ? "patient" : "doctor" });
        setTagInput("");
    }, [open, contact, defaultChannel]);

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    const addTag = () => {
        const t = tagInput.trim().toLowerCase();
        if (!t || form.tags.includes(t)) return setTagInput("");
        setForm((f) => ({ ...f, tags: [...f.tags, t] }));
        setTagInput("");
    };
    const removeTag = (t) => setForm((f) => ({ ...f, tags: f.tags.filter((x) => x !== t) }));

    const submit = async () => {
        if (!form.name.trim()) return toast.error("Name is required");
        setBusy(true);
        try {
            const payload = {
                ...form,
                estimated_value: parseFloat(form.estimated_value) || 0,
                assigned_mr_id: form.assigned_mr_id || null,
                next_follow_up_at: form.next_follow_up_at || null,
            };
            if (contact) {
                await api.put(`/crm/contacts/${contact.id}`, payload);
                toast.success(`'${form.name}' updated`);
            } else {
                await api.post("/crm/contacts", payload);
                toast.success(`'${form.name}' added to CRM`);
            }
            onOpenChange(false);
            onSaved?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Couldn't save contact");
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="rounded-none max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="crm-contact-dialog">
                <DialogHeader>
                    <DialogTitle>{contact ? "Edit CRM contact" : "New CRM contact"}</DialogTitle>
                    <DialogDescription>
                        {contact ? "Update the details below. Activity-logged for admins." : "Add a doctor, distributor or prospect to your CRM funnel."}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <Field label="Channel" hint={form.channel === "b2b" ? "Trade — doctor, distributor, hospital, retailer" : "E-commerce — website retail customer"}>
                            <Select value={form.channel} onValueChange={(v) => set("channel", v)}>
                                <SelectTrigger className="rounded-none border-stone-300" data-testid="crm-form-channel"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="b2b">B2B · Trade</SelectItem>
                                    <SelectItem value="ecommerce">E-commerce · Retail</SelectItem>
                                </SelectContent>
                            </Select>
                        </Field>
                        <Field label="Type">
                            <Select value={form.contact_type} onValueChange={(v) => set("contact_type", v)}>
                                <SelectTrigger className="rounded-none border-stone-300" data-testid="crm-form-type"><SelectValue /></SelectTrigger>
                                <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                            </Select>
                        </Field>
                    </div>

                    <Field label="Name" required><Input value={form.name} onChange={(e) => set("name", e.target.value)} className="rounded-none border-stone-300" data-testid="crm-form-name" /></Field>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <Field label="Phone"><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} className="rounded-none border-stone-300" data-testid="crm-form-phone" /></Field>
                        <Field label="WhatsApp"><Input value={form.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} className="rounded-none border-stone-300" data-testid="crm-form-whatsapp" /></Field>
                        <Field label="Email"><Input value={form.email} onChange={(e) => set("email", e.target.value)} className="rounded-none border-stone-300" data-testid="crm-form-email" /></Field>
                    </div>

                    <Field label="Company / Clinic name"><Input value={form.company} onChange={(e) => set("company", e.target.value)} className="rounded-none border-stone-300" data-testid="crm-form-company" /></Field>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <Field label="City"><Input value={form.city} onChange={(e) => set("city", e.target.value)} className="rounded-none border-stone-300" data-testid="crm-form-city" /></Field>
                        <Field label="State"><Input value={form.state} onChange={(e) => set("state", e.target.value)} className="rounded-none border-stone-300" data-testid="crm-form-state" /></Field>
                    </div>

                    <Field label="Address"><Input value={form.address} onChange={(e) => set("address", e.target.value)} className="rounded-none border-stone-300" data-testid="crm-form-address" /></Field>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <Field label="Pipeline stage">
                            <Select value={form.stage} onValueChange={(v) => set("stage", v)}>
                                <SelectTrigger className="rounded-none border-stone-300" data-testid="crm-form-stage"><SelectValue /></SelectTrigger>
                                <SelectContent>{STAGES.map((s) => <SelectItem key={s} value={s}>{stageLabel(form.channel, s)}</SelectItem>)}</SelectContent>
                            </Select>
                        </Field>
                        <Field label="Source">
                            <Select value={form.source || "_none"} onValueChange={(v) => set("source", v === "_none" ? "" : v)}>
                                <SelectTrigger className="rounded-none border-stone-300" data-testid="crm-form-source"><SelectValue placeholder="—" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="_none">—</SelectItem>
                                    {SOURCES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </Field>
                        <Field label="Est. monthly Rs"><Input type="number" min={0} value={form.estimated_value} onChange={(e) => set("estimated_value", e.target.value)} className="rounded-none border-stone-300" data-testid="crm-form-value" /></Field>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <Field label="Assigned MR" hint={!canReassign ? "Only admin/editor can reassign" : null}>
                            <Select value={form.assigned_mr_id || "_unassigned"} onValueChange={(v) => set("assigned_mr_id", v === "_unassigned" ? "" : v)} disabled={!canReassign && !!contact}>
                                <SelectTrigger className="rounded-none border-stone-300" data-testid="crm-form-mr"><SelectValue placeholder="—" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="_unassigned">— Unassigned —</SelectItem>
                                    {mrs.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}{m.territory ? ` · ${m.territory}` : ""}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </Field>
                        <Field label="Next follow-up date"><Input type="date" value={form.next_follow_up_at || ""} onChange={(e) => set("next_follow_up_at", e.target.value)} className="rounded-none border-stone-300" data-testid="crm-form-followup" /></Field>
                    </div>

                    <Field label="Tags" hint="Type and press Enter (e.g. high-value, season-1, hospital-chain)">
                        <div className="flex flex-wrap gap-1.5 mb-2">
                            {form.tags.map((t) => (
                                <span key={t} className="inline-flex items-center gap-1 bg-sky-100 text-sky-800 px-2 py-1 text-xs">
                                    {t}
                                    <button onClick={() => removeTag(t)} className="hover:text-red-700"><X className="w-3 h-3" /></button>
                                </span>
                            ))}
                        </div>
                        <Input
                            value={tagInput}
                            onChange={(e) => setTagInput(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }}
                            placeholder="Add tag and press Enter"
                            className="rounded-none border-stone-300"
                            data-testid="crm-form-tags"
                        />
                    </Field>

                    <Field label="Notes"><Textarea rows={3} value={form.notes} onChange={(e) => set("notes", e.target.value)} className="rounded-none border-stone-300" data-testid="crm-form-notes" /></Field>
                </div>

                <DialogFooter className="border-t border-stone-200 pt-3">
                    <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-none" data-testid="crm-form-cancel">Cancel</Button>
                    <Button onClick={submit} disabled={busy} className="rounded-none bg-sky-700 hover:bg-sky-800 text-white" data-testid="crm-form-save">
                        <Save className="w-4 h-4 mr-2" />{busy ? "Saving…" : (contact ? "Save changes" : "Add contact")}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

const Field = ({ label, required, hint, children }) => (
    <div>
        <Label className="text-[10px] uppercase tracking-widest text-stone-500">
            {label}{required && <span className="text-red-700 ml-0.5">*</span>}
        </Label>
        <div className="mt-1">{children}</div>
        {hint && <p className="text-[10px] text-stone-500 mt-1">{hint}</p>}
    </div>
);
